import { Injectable } from '@angular/core';
import { HealthModels } from 'models/health.models';
import { PolicyModels } from 'models/policy.models';
import { QuotationModels } from 'models/quotation.models';

@Injectable({
  providedIn: 'root',
})
export class HealthRepositoryService {
  getFormulaInformations(
    formula: QuotationModels.FormulaEnum,
    packageId: PolicyModels.PackageEnum,
  ): {
    withDPTAM: HealthModels.FormulaUniversBesoin[];
    withoutDPTAM: HealthModels.FormulaUniversBesoin[];
  } {
    let storage: {
      [formula: number]: HealthModels.FormulaUniversBesoinStorage[];
    };
    let limits: { [ubType: number]: HealthModels.UniversBesoinMetrics };

    if (packageId == PolicyModels.PackageEnum.Sante_InfinitySante) {
      storage = formulaInformationsStorageInfinity;
      limits = infinityLimits;
    } else if (packageId == PolicyModels.PackageEnum.Sante_Acheel) {
      storage = formulaInformationsStorageAcheel;
      limits = acheelLimits;
    } else {
      storage = formulaInformationsStorageFidelity;
      limits = fidelityLimits;
    }
    const ubs = storage[+formula];
    if (!ubs) {
      throw Error(`No information in storage for formula ${formula}`);
    }

    const withDPTAM = [
      ...ubs.map((ub) =>
        Object.assign({}, { ...limits[ub.universType] }, ub, {
          value: this.computeValue(ub, true),
          valueMetricType:
            ub.valueMetricType || limits[ub.universType].headerMetricType,
        }),
      ),
    ];

    const withoutDPTAM = [
      ...ubs.map((ub) =>
        Object.assign({}, { ...limits[ub.universType] }, ub, {
          value: this.computeValue(ub, false),
          valueMetricType:
            ub.valueMetricType || limits[ub.universType].headerMetricType,
        }),
      ),
    ];

    return { withDPTAM, withoutDPTAM };
  }

  computeValue(
    ub: HealthModels.FormulaUniversBesoinStorage,
    isDPTAM: boolean,
  ): number {
    if (typeof ub.value === 'number') {
      return ub.value;
    }

    return isDPTAM ? ub.value.withDPTAM : ub.value.withoutDPTAM;
  }
}

const infinityLimits: { [ubType: number]: HealthModels.UniversBesoinMetrics } =
  {
    [HealthModels.UniversBesoinTypeEnum.Hospitalisation]: {
      maximum: 350,
      headerMetricType: 'percentage',
    },
    [HealthModels.UniversBesoinTypeEnum.Medecine]: {
      maximum: 300,
      headerMetricType: 'percentage',
    },
    [HealthModels.UniversBesoinTypeEnum.Pharmacie]: {
      maximum: 100,
      headerMetricType: 'percentage',
    },
    [HealthModels.UniversBesoinTypeEnum.Dentaire]: {
      maximum: 400,
      headerMetricType: 'percentage',
    },
    [HealthModels.UniversBesoinTypeEnum.Optique]: {
      maximum: 500,
      headerMetricType: 'euro',
    },
    [HealthModels.UniversBesoinTypeEnum.BienEtre]: {
      maximum: 250,
      headerMetricType: 'euro',
    },
    [HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC]: {
      maximum: 3000,
      headerMetricType: 'euro',
    },
  };

const acheelLimits: { [ubType: number]: HealthModels.UniversBesoinMetrics } = {
  [HealthModels.UniversBesoinTypeEnum.Hospitalisation]: {
    maximum: 350,
    headerMetricType: 'percentage',
  },
  [HealthModels.UniversBesoinTypeEnum.Medecine]: {
    maximum: 300,
    headerMetricType: 'percentage',
  },
  [HealthModels.UniversBesoinTypeEnum.Pharmacie]: {
    maximum: 100,
    headerMetricType: 'percentage',
  },
  [HealthModels.UniversBesoinTypeEnum.Dentaire]: {
    maximum: 400,
    headerMetricType: 'percentage',
  },
  [HealthModels.UniversBesoinTypeEnum.Optique]: {
    maximum: 500,
    headerMetricType: 'euro',
  },
  [HealthModels.UniversBesoinTypeEnum.BienEtre]: {
    maximum: 250,
    headerMetricType: 'euro',
  },
  [HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC]: {
    maximum: 3000,
    headerMetricType: 'euro',
  },
  [HealthModels.UniversBesoinTypeEnum.Auditif]: {
    maximum: 600,
    headerMetricType: 'euro',
  },
};

const fidelityLimits: { [ubType: number]: HealthModels.UniversBesoinMetrics } =
  {
    [HealthModels.UniversBesoinTypeEnum.Hospitalisation]: {
      maximum: 300,
      headerMetricType: 'percentage',
    },
    [HealthModels.UniversBesoinTypeEnum.Medecine]: {
      maximum: 250,
      headerMetricType: 'percentage',
    },
    [HealthModels.UniversBesoinTypeEnum.Pharmacie]: {
      maximum: 100,
      headerMetricType: 'percentage',
    },
    [HealthModels.UniversBesoinTypeEnum.Dentaire]: {
      maximum: 300,
      headerMetricType: 'percentage',
    },
    [HealthModels.UniversBesoinTypeEnum.Optique]: {
      maximum: 400,
      headerMetricType: 'euro',
    },
    [HealthModels.UniversBesoinTypeEnum.BienEtre]: {
      maximum: 250,
      headerMetricType: 'euro',
    },
    [HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC]: {
      maximum: 3000,
      headerMetricType: 'euro',
    },
  };

const formulaInformationsStorageInfinity: {
  [formula: number]: HealthModels.FormulaUniversBesoinStorage[];
} = {
  [QuotationModels.FormulaEnum.Sante1]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
      valueMetricType: 'percentage',
    },
  ],
  [QuotationModels.FormulaEnum.Sante2]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 125, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 150,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 200,
    },
  ],
  [QuotationModels.FormulaEnum.Sante3]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 150, withoutDPTAM: 105 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 125, withoutDPTAM: 105 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 200,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 250,
    },
  ],
  [QuotationModels.FormulaEnum.Sante4]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 175, withoutDPTAM: 130 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 150, withoutDPTAM: 130 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 250,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 300,
    },
  ],
  [QuotationModels.FormulaEnum.Sante5]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 200, withoutDPTAM: 155 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 175, withoutDPTAM: 155 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 275,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 350,
    },
  ],
  [QuotationModels.FormulaEnum.Sante6]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 250, withoutDPTAM: 180 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 200, withoutDPTAM: 180 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 300,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 400,
    },
  ],
  [QuotationModels.FormulaEnum.Sante7]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 300, withoutDPTAM: 200 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 250, withoutDPTAM: 200 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 350,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 450,
    },
  ],
};

const formulaInformationsStorageAcheel: {
  [formula: number]: HealthModels.FormulaUniversBesoinStorage[];
} = {
  [QuotationModels.FormulaEnum.Sante1]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
      valueMetricType: 'percentage',
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Auditif,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
      valueMetricType: 'percentage',
    },
  ],
  [QuotationModels.FormulaEnum.Sante2]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 125, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 150,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 200,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Auditif,
      value: 100,
    },
  ],
  [QuotationModels.FormulaEnum.Sante3]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 150, withoutDPTAM: 105 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 125, withoutDPTAM: 105 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 200,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 250,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Auditif,
      value: 200,
    },
  ],
  [QuotationModels.FormulaEnum.Sante4]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 175, withoutDPTAM: 130 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 150, withoutDPTAM: 130 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 250,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 300,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Auditif,
      value: 300,
    },
  ],
  [QuotationModels.FormulaEnum.Sante5]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 200, withoutDPTAM: 155 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 175, withoutDPTAM: 155 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 275,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 350,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Auditif,
      value: 400,
    },
  ],
  [QuotationModels.FormulaEnum.Sante6]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 250, withoutDPTAM: 180 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 200, withoutDPTAM: 180 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 300,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 400,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Auditif,
      value: 500,
    },
  ],
  [QuotationModels.FormulaEnum.Sante7]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 300, withoutDPTAM: 200 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 250, withoutDPTAM: 200 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 350,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 450,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Auditif,
      value: 600,
    },
  ],
};

const formulaInformationsStorageFidelity: {
  [formula: number]: HealthModels.FormulaUniversBesoinStorage[];
} = {
  [QuotationModels.FormulaEnum.Sante1]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
      valueMetricType: 'percentage',
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.BienEtre,
      value: { withDPTAM: 50, withoutDPTAM: 50 },
      valueMetricType: 'euro',
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC,
      value: { withDPTAM: 3000, withoutDPTAM: 3000 },
      valueMetricType: 'euro',
    },
  ],
  [QuotationModels.FormulaEnum.Sante2]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 125, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 150, withoutDPTAM: 100 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 150,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 250,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.BienEtre,
      value: { withDPTAM: 75, withoutDPTAM: 75 },
      valueMetricType: 'euro',
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC,
      value: { withDPTAM: 3000, withoutDPTAM: 3000 },
      valueMetricType: 'euro',
    },
  ],
  [QuotationModels.FormulaEnum.Sante3]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 150, withoutDPTAM: 125 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 175, withoutDPTAM: 125 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 200,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 300,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.BienEtre,
      value: { withDPTAM: 100, withoutDPTAM: 100 },
      valueMetricType: 'euro',
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC,
      value: { withDPTAM: 3000, withoutDPTAM: 3000 },
      valueMetricType: 'euro',
    },
  ],
  [QuotationModels.FormulaEnum.Sante4]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 200, withoutDPTAM: 175 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 200, withoutDPTAM: 150 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 250,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 350,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.BienEtre,
      value: { withDPTAM: 150, withoutDPTAM: 150 },
      valueMetricType: 'euro',
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC,
      value: { withDPTAM: 3000, withoutDPTAM: 3000 },
      valueMetricType: 'euro',
    },
  ],
  [QuotationModels.FormulaEnum.Sante5]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 250, withoutDPTAM: 200 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 225, withoutDPTAM: 175 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 275,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 375,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.BienEtre,
      value: { withDPTAM: 200, withoutDPTAM: 200 },
      valueMetricType: 'euro',
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC,
      value: { withDPTAM: 3000, withoutDPTAM: 3000 },
      valueMetricType: 'euro',
    },
  ],
  [QuotationModels.FormulaEnum.Sante6]: [
    {
      universType: HealthModels.UniversBesoinTypeEnum.Hospitalisation,
      value: { withDPTAM: 300, withoutDPTAM: 200 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Medecine,
      value: { withDPTAM: 250, withoutDPTAM: 200 },
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Pharmacie,
      value: 100,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Dentaire,
      value: 300,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.Optique,
      value: 400,
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.BienEtre,
      value: { withDPTAM: 250, withoutDPTAM: 250 },
      valueMetricType: 'euro',
    },
    {
      universType: HealthModels.UniversBesoinTypeEnum.AvantageCapitalDC,
      value: { withDPTAM: 3000, withoutDPTAM: 3000 },
      valueMetricType: 'euro',
    },
  ],
};
